import { Button } from "@/components/ui/button";
import { ArrowsClockwise, Plus } from "@phosphor-icons/react";

/**
 * Per-network bulk-action button row that sits between the
 * NetworkHubPanel and the catalog search bar. Buttons appear /
 * disappear based on `networkFilter` so the admin only sees actions
 * for the currently-picked source feed (no more 9-button wall).
 *
 * All handlers + loading flags live on the parent — this is a pure
 * presentational shell so the per-feed state stays co-located with
 * its data-fetch logic.
 */
export default function NetworkActionsBar({
  networkFilter,
  onOpenPartnerizeImport,
  onOpenSkimlinksImport,
  onOpenMrgeImport,
  onRefreshSkim, refreshingSkim,
  onSyncSkimReports, syncingSkimReports,
  onSyncSkimPayments, syncingSkimPayments,
  onMarkSkimNc, markingSkimNc,
  onBackfillMrge, backfillingMrge,
  onDeepRefreshMrge, mrgeRateRun,
  onImportCj,
  onSyncShopnomix,
  syncingShopnomix,
  onSyncShopnomixReports,
  syncingShopnomixReports,
  onSyncPronomix,
  syncingPronomix,
  onAddManual,
}) {
  const all = networkFilter === "all";
  const showPartnerize = all || networkFilter === "partnerize";
  const showSkimlinks = all || networkFilter === "skimlinks";
  const showMrge = all || networkFilter === "mrge";
  const showCj = all || networkFilter === "cj";
  const showShopnomix = all || networkFilter === "shopnomix";
  const showPronomix = all || networkFilter === "pronomix";
  const showManualAdd = all || networkFilter === "pubadhub" || networkFilter === "pubadhub_kevin" || networkFilter === "shopnomix_kevin";

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="network-actions">
      {showPartnerize && (
        <Button
          variant="outline" size="sm" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          onClick={onOpenPartnerizeImport}
          data-testid="import-partnerize-btn"
        >
          <Plus size={14} className="mr-1" /> Bulk import from Partnerize
        </Button>
      )}
      {showSkimlinks && (
        <>
          <Button
            variant="outline" size="sm" className="border-orange-200 text-orange-700 hover:bg-orange-50"
            onClick={onOpenSkimlinksImport}
            data-testid="import-skimlinks-btn"
          >
            <Plus size={14} className="mr-1" /> Bulk import from Skimlinks
          </Button>
          <Button variant="ghost" size="sm" className="text-orange-700 hover:bg-orange-50" disabled={refreshingSkim} onClick={onRefreshSkim} data-testid="refresh-skimlinks-rates-btn">
            <ArrowsClockwise size={12} className={`mr-1 ${refreshingSkim ? "animate-spin" : ""}`} />
            {refreshingSkim ? "Refreshing…" : "Refresh rates"}
          </Button>
          <Button variant="ghost" size="sm" className="text-orange-700 hover:bg-orange-50" disabled={syncingSkimReports} onClick={onSyncSkimReports} data-testid="sync-skimlinks-reports-btn">
            <ArrowsClockwise size={12} className={`mr-1 ${syncingSkimReports ? "animate-spin" : ""}`} />
            {syncingSkimReports ? "Syncing…" : "Sync reports"}
          </Button>
          <Button variant="ghost" size="sm" className="text-orange-700 hover:bg-orange-50" disabled={syncingSkimPayments} onClick={onSyncSkimPayments} data-testid="sync-skimlinks-payments-btn">
            <ArrowsClockwise size={12} className={`mr-1 ${syncingSkimPayments ? "animate-spin" : ""}`} />
            {syncingSkimPayments ? "Syncing…" : "Sync payments"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-orange-700 hover:bg-orange-50"
            disabled={markingSkimNc}
            onClick={onMarkSkimNc}
            title="Mark ~35% of PENDING Skimlinks conversions as Non-Commission / Rejected. Updates status only — never deletes."
            data-testid="mark-skimlinks-nc-btn"
          >
            <ArrowsClockwise size={12} className={`mr-1 ${markingSkimNc ? "animate-spin" : ""}`} />
            {markingSkimNc ? "Marking…" : "Mark ~35% pending NC"}
          </Button>
        </>
      )}
      {showMrge && (
        <>
          <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={onOpenMrgeImport} data-testid="import-mrge-btn">
            <Plus size={14} className="mr-1" /> Bulk import from mrge
          </Button>
          <Button
            variant="ghost" size="sm" className="text-emerald-700 hover:bg-emerald-50"
            disabled={backfillingMrge}
            onClick={onBackfillMrge}
            title="Re-parse the catalog-level rate string for every mrge brand (fast, no API calls)"
            data-testid="backfill-mrge-rates-btn"
          >
            <ArrowsClockwise size={12} className={`mr-1 ${backfillingMrge ? "animate-spin" : ""}`} />
            {backfillingMrge ? "Backfilling…" : "Backfill rates (fast)"}
          </Button>
          <Button
            variant="ghost" size="sm" className="text-emerald-700 hover:bg-emerald-50"
            disabled={!!mrgeRateRun && mrgeRateRun.status === "running"}
            onClick={() => onDeepRefreshMrge(true)}
            title="Background job — pulls /advertiser/rates/{id} for every brand still missing a rate. Concurrency 10, ~15 min for 20k brands."
            data-testid="deep-refresh-mrge-rates-btn"
          >
            <ArrowsClockwise size={12} className={`mr-1 ${mrgeRateRun?.status === "running" ? "animate-spin" : ""}`} />
            {mrgeRateRun?.status === "running"
              ? `Deep refresh… ${mrgeRateRun?.counts?.processed || 0}/${mrgeRateRun?.total_brands || "?"}`
              : "Deep refresh rates (bg)"}
          </Button>
        </>
      )}
      {showCj && (
        <Button
          variant="outline" size="sm" className="border-amber-200 text-amber-700 hover:bg-amber-50"
          onClick={onImportCj}
          data-testid="import-cj-btn"
        >
          <Plus size={14} className="mr-1" /> Import joined CJ brands
        </Button>
      )}
      {showShopnomix && (
        <>
          <Button
            variant="outline" size="sm" className="border-lime-200 text-lime-800 hover:bg-lime-50"
            disabled={syncingShopnomix}
            onClick={onSyncShopnomix}
            data-testid="sync-shopnomix-catalog-btn"
          >
            <ArrowsClockwise size={14} className={`mr-1 ${syncingShopnomix ? "animate-spin" : ""}`} />
            {syncingShopnomix ? "Syncing Shopnomix…" : "Sync Shopnomix catalog"}
          </Button>
          <Button
            variant="ghost" size="sm" className="text-lime-800 hover:bg-lime-50"
            disabled={syncingShopnomixReports}
            onClick={onSyncShopnomixReports}
            title="Pull Shopnomix CPA conversions into Admin Reports (last 30 days)"
            data-testid="sync-shopnomix-reports-btn"
          >
            <ArrowsClockwise size={12} className={`mr-1 ${syncingShopnomixReports ? "animate-spin" : ""}`} />
            {syncingShopnomixReports ? "Syncing reports…" : "Sync reports (30d)"}
          </Button>
        </>
      )}
      {showPronomix && (
        <Button
          variant="outline" size="sm" className="border-purple-200 text-purple-800 hover:bg-purple-50"
          disabled={syncingPronomix}
          onClick={onSyncPronomix}
          data-testid="sync-pronomix-catalog-btn"
        >
          <ArrowsClockwise size={14} className={`mr-1 ${syncingPronomix ? "animate-spin" : ""}`} />
          {syncingPronomix ? "Syncing Pronomix…" : "Sync Pronomix catalog"}
        </Button>
      )}
      {showManualAdd && (
        <Button
          variant="outline" size="sm"
          className="border-slate-300 text-slate-700 hover:bg-slate-50"
          onClick={onAddManual}
          data-testid="add-manual-brand-btn"
        >
          <Plus size={14} className="mr-1" /> Add brand manually
        </Button>
      )}
    </div>
  );
}
